import React, { Suspense, useState } from "react";

import * as API from "@ekonsilio/api-livechat";
import PropTypes from "prop-types";
import { useQuery } from "react-query";

import { useSDK } from "../../utils/sdk";
import { useWebmessenger } from "../../utils/webmessenger";
import Markdown from "../markdown";

const PanelInformations = React.lazy(() => import("./PanelInformations"));
const PrivacyPolicy = React.lazy(() => import("./PrivacyPolicy"));
const CloseModal = React.lazy(() => import("./CloseModal"));

const webmessengerApi = new API.Messaging.Webmessenger();

function Header({ onMinimze, onToggleSoundNotification, soundNotification }) {
    const {
        greeting,
        operator,
        setOperator,
        conversation,
        integration,
        online,
        hide,
    } = useWebmessenger();
    const { emit } = useSDK();

    const [informationsPanel, setInformationsPanel] = useState(false);
    const [privacyShow, setPrivacyShow] = useState(false);
    const [closeModal, setCloseModal] = useState(false);

    useQuery(
        ["operator"],
        async () =>
            await webmessengerApi.getOperator(
                integration,
                operator?._id,
                operator?.avatar
            ),
        {
            onSuccess: (data) => {
                setOperator(data);
            },
            enabled: conversation === null,
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );
    return (
        <div className="ek-chat__header ek-chat__header--end">
            {closeModal && (
                <Suspense fallback={<span />}>
                    <CloseModal
                        onClose={() => {
                            setCloseModal(false);
                            if (!online) {
                                hide();
                            }
                        }}
                        onCancel={() => {
                            setCloseModal(false);
                        }}
                    />
                </Suspense>
            )}

            {informationsPanel && (
                <Suspense fallback={<span />}>
                    <PanelInformations
                        onClosePanel={() => {
                            setInformationsPanel(false);
                        }}
                        openCloseModal={() => {
                            setCloseModal(true);
                        }}
                        onClosePolicy={() => {
                            if (privacyShow) {
                                setPrivacyShow(false);
                            } else setPrivacyShow(true);
                        }}
                        soundNotification={soundNotification}
                        onToggleSoundNotification={onToggleSoundNotification}
                    />
                </Suspense>
            )}

            <div className="ek-chat__header--container">
                <div>
                    <div className="ek-chat__header--infos">
                        {operator && (
                            <div className="ek-chat__header--operator">
                                {operator.face && (
                                    <span className="ek-chat__avatar">
                                        <img
                                            src={
                                                operator.type === "agent"
                                                    ? `${process.env.REACT_APP_FILE}/files/agents-logo/${operator.face}`
                                                    : `${process.env.REACT_APP_FILE}/files/avatars/${operator.face}`
                                            }
                                            alt=""
                                            className="ek-chat__avatar--img"
                                        />
                                    </span>
                                )}
                                {(!greeting?.header || conversation) && (
                                    <div className="ek-chat__header--operator--infos">
                                        <span className="ek-chat__header--operator--infos--name">
                                            {operator.name}
                                        </span>
                                        <span className="ek-chat__header--operator--infos--title">
                                            {operator.title}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="ek-chat__header--controls">
                        <button
                            type="button"
                            className="ek-chat--button"
                            id="informationPanelButton"
                            onClick={() =>
                                setInformationsPanel(!informationsPanel)
                            }
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                            >
                                <g
                                    fill="none"
                                    fillRule="evenodd"
                                    stroke="none"
                                    strokeWidth="1"
                                >
                                    <rect
                                        width="3"
                                        height="3"
                                        x="10"
                                        y="4"
                                        fill="currentColor"
                                        rx="1"
                                    />
                                    <rect
                                        width="3"
                                        height="3"
                                        x="10"
                                        y="10"
                                        fill="currentColor"
                                        rx="1"
                                    />
                                    <rect
                                        width="3"
                                        height="3"
                                        x="10"
                                        y="16"
                                        fill="currentColor"
                                        rx="1"
                                    />
                                </g>
                            </svg>
                        </button>
                        <button
                            type="button"
                            className="ek-chat--button"
                            onClick={() => {
                                emit("window:minimized", {
                                    conversation: conversation?._id || null,
                                });
                                sessionStorage.setItem(
                                    "eK-chat-button-animation",
                                    JSON.stringify(false)
                                );
                                onMinimze();
                            }}
                        >
                            <svg viewBox="0 0 24 24" version="1.1">
                                <g
                                    stroke="none"
                                    strokeWidth="1"
                                    fill="none"
                                    fillRule="evenodd"
                                >
                                    <rect
                                        fill="currentColor"
                                        x="4"
                                        y="11"
                                        width="16"
                                        height="2"
                                        rx="1"
                                    />
                                </g>
                            </svg>
                        </button>
                    </div>
                </div>
                {greeting?.header && !conversation && (
                    <div className="ek-chat__header--greeting">
                        <span className="ek-chat__header--greeting--title">
                            {greeting.header?.title}
                        </span>
                        <Markdown content={greeting.header?.text} greeting />
                    </div>
                )}
            </div>
            {privacyShow && (
                <>
                    <button
                        type="button"
                        onClick={() => setPrivacyShow(false)}
                        className="ek-chat__header--hide-privacyPolicy ek-chat--button"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                        >
                            <g
                                fill="none"
                                fillRule="evenodd"
                                stroke="none"
                                strokeWidth="1"
                                transform="translate(12.000000, 12.000000) rotate(-45.000000) translate(-12.000000, -12.000000) translate(4.000000, 4.000000)"
                            >
                                <rect
                                    transform="translate(8.000000, 8.000000) rotate(-270.000000) translate(-8.000000, -8.000000) "
                                    x="5"
                                    y="4"
                                    width="8"
                                    height="2"
                                    rx="1"
                                    fill="currentColor"
                                />
                                <rect
                                    x="4"
                                    y="5"
                                    width="8"
                                    height="2"
                                    rx="1"
                                    fill="currentColor"
                                />
                            </g>
                        </svg>
                    </button>
                    <Suspense fallback={<span />}>
                        <PrivacyPolicy showPrivacy={privacyShow} />
                    </Suspense>
                </>
            )}
        </div>
    );
}

Header.propTypes = {
    onMinimze: PropTypes.func,
    onToggleSoundNotification: PropTypes.func,
    soundNotification: PropTypes.bool,
};

Header.defaultProps = {
    onMinimze: () => {},
    onToggleSoundNotification: () => {},
    soundNotification: false,
};

export default Header;
